<template>
  <form class="mx-auto">
    <FormHeader :title="title" @cancel="onCancel" @submit="onSubmit" />

    <div class="space-y-12 mt-5">
      <div class="border-b border-gray-900/10 pb-12">
        <!-- General fields -->
        <FormTextField
          v-model="fileId"
          field-path="attributes.fileId"
          :field-label="$gettext('Numéro de dossier')"
          :errors="errors"
        />
        <FormTextField
          v-model="commission"
          field-path="attributes.commission"
          :field-label="$gettext('Commission')"
          :errors="errors"
        />
        <FormSelectField
          v-model="commissionTypeId"
          field-path="relationships.commissionType.data.id"
          :field-label="$gettext('Type de commission')"
          :errors="errors"
          :options="commissionTypeOptions"
        />
        <FormDateField
          v-model="endAt"
          field-path="attributes.endAt"
          field-label="Date de fin"
          :errors="errors"
        />
        <FormCustomerComboboxField
          v-model="rentalListingAgreementLandlords"
          field-path="relationships.rentalListingAgreementLandlords.data"
          :field-label="$gettext('Bailleur(s)')"
          :errors="errors"
        />

        <!-- Properties -->
        <div class="sm:grid sm:grid-cols-3 sm:items-start sm:gap-4 sm:py-2">
          <label class="block text-sm/6 font-medium text-gray-900 sm:pt-1.5">
            {{ $gettext('Propriétés listées') }}
          </label>
          <div class="mt-2 sm:col-span-2 sm:mt-0 space-y-4">
            <RentalListingAgreementPropertyForm
              v-for="(field, index) in rentalListingAgreementProperties"
              :key="field.value.id || field.value._tempKey"
              v-model="field.value"
              :index="index"
              :errors="errorsFor(index)"
              @remove="removeListedProperty(index)"
            />
            <button
              @click.prevent="addListedProperty"
              class="text-indigo-600 hover:text-indigo-900 transition-transform duration-200 hover:scale-110 flex items-center gap-2"
            >
              <PlusCircleIcon class="size-7" />
              {{ $gettext('Ajouter une propriété') }}
            </button>
            <p
              v-if="errors['relationships.rentalListingAgreementProperties.data']"
              class="text-sm text-red-600"
            >
              {{ errors['relationships.rentalListingAgreementProperties.data'] }}
            </p>
          </div>
        </div>

        <!-- Documents -->
        <div class="border-b border-gray-900/10 pb-12 mt-6">
          <h3 class="text-base/7 font-semibold text-gray-900">
            {{ $gettext("Documents à importer") }}
          </h3>
          <FormDocumentsField
            v-model="rentalListingAgreementDocuments"
            category-name="rentalListingAgreement"
            field-path="relationships.rentalListingAgreementDocuments.data"
            :field-label="$gettext('Mission de location')"
            :errors="errors"
          />
        </div>
      </div>
    </div>

    <FormFooter @cancel="onCancel" @submit="onSubmit" />
  </form>
</template>

<script setup>
import { useForm, useFieldArray } from "vee-validate";
import * as yup from "yup";
import { computed, watch } from "vue";
import FormHeader from "@/components/form-fields/FormHeader.vue";
import FormFooter from "@/components/form-fields/FormFooter.vue";
import FormTextField from "@/components/form-fields/FormTextField.vue";
import FormDateField from "@/components/form-fields/FormDateField.vue";
import FormSelectField from "@/components/form-fields/FormSelectField.vue";
import FormCustomerComboboxField from "@/components/form-fields/FormCustomerComboboxField.vue";
import FormDocumentsField from "@/components/form-fields/FormDocumentsField.vue";
import { PlusCircleIcon } from "@heroicons/vue/24/outline";
import RentalListingAgreementPropertyForm from "@/components/rental-listing-agreements/RentalListingAgreementPropertyForm.vue";
import { useCommissionTypeStore } from "@/stores/commissionType";
import { useGettext } from "vue3-gettext";

const commissionTypeStore = useCommissionTypeStore();
const { $gettext } = useGettext();

const props = defineProps(["rentalListingAgreement", "title"]);
const emits = defineEmits(["submit", "cancel"]);

const validationSchema = yup.object({
  attributes: yup.object({
    commission: yup.number().typeError($gettext("doit être un nombre")).required($gettext("est obligatoire")),
    endAt: yup.date().required($gettext("est obligatoire"))
  }),
  relationships: yup.object({
    rentalListingAgreementLandlords: yup.object({
      data: yup.array().of(yup.object({ id: yup.string() })).min(1, $gettext("au moins 1 obligatoire"))
    }),
    rentalListingAgreementProperties: yup.object({
      data: yup.array().of(
        yup.object({
          id: yup.string().nullable(),
          attributes: yup.object({
            listingRent: yup.number().typeError($gettext("doit être un nombre")).positive($gettext("doit être positif")).required($gettext("est obligatoire"))
          }),
          relationships: yup.object({
            property: yup.object({
              data: yup.object().required($gettext("est obligatoire")).test("has-id", $gettext("est obligatoire"), val => !!val?.id)
            })
          }),
        })
      ).min(1, $gettext("au moins 1 obligatoire"))
    })
  })
});

const { errors, handleSubmit, defineField, setValues } = useForm({
  validationSchema,
  initialValues: props.rentalListingAgreement || {}
});

const [fileId] = defineField("attributes.fileId");
const [commission] = defineField("attributes.commission");
const [commissionTypeId] = defineField("relationships.commissionType.data.id");
const [rentalListingAgreementLandlords] = defineField("relationships.rentalListingAgreementLandlords.data");
const { fields: rentalListingAgreementProperties, push, remove } = useFieldArray("relationships.rentalListingAgreementProperties.data");
const [endAt] = defineField("attributes.endAt");
const [rentalListingAgreementDocuments] = defineField("relationships.rentalListingAgreementDocuments.data");

const commissionTypeOptions = computed(() =>
  commissionTypeStore.all.map(ct => ({ value: ct.id, label: ct.attributes.name }))
);

const addListedProperty = () => {
  push({
    _tempKey: crypto.randomUUID(),
    id: null,
    type: "rentalListingAgreementProperty",
    attributes: { listingRent: null, details: null },
    relationships: { property: { data: { id: null, attributes: { fullAddress: null } } } }
  });
};

const errorsFor = (index) => {
  const prefix = `relationships.rentalListingAgreementProperties.data[${index}].`;
  return Object.fromEntries(
    Object.entries(errors.value).flatMap(([key, val]) => {
      if (key.startsWith(prefix)) {
        let error = [[key.slice(prefix.length), val]];
        return error
      }
      return [];
    })
  );
}

const removeListedProperty = (index) => remove(index);

const stripCustomerObjects = (customers) => {
  if (!Array.isArray(customers)) return customers;
  return customers.map(({ id, type }) => ({ id, type }));
};

const onFormSubmit = (values, actions) => {
  const payload = {
    ...values,
    relationships: {
      ...values.relationships,
      rentalListingAgreementLandlords: {
        ...values.relationships?.rentalListingAgreementLandlords,
        data: stripCustomerObjects(values.relationships?.rentalListingAgreementLandlords?.data)
      }
    }
  };
  emits("submit", payload, actions);
};
const onSubmit = () => handleSubmit(onFormSubmit)();
const onCancel = () => emits("cancel");

watch(
  () => props.rentalListingAgreement,
  (newVal) => {
    if (newVal) {
      setValues(newVal);
    }
  },
  { immediate: true }
);
</script>
